import { range } from './array.js';
import { getOwnerDocument, getOwnerWindow } from './dom.js';
import { addListener } from './event.js';

// There are 3 different states:
// 1. fully visible (intersectionRatio === 1) - nothing to do
// 2. partially visible (0 < intersectionRatio < 1) - reposition
// 3. not visible (intersectionRatio === 0) - reposition
//
// 2 can happen if the element is clipped by a scroll container. In this case, we might
// be better off updating the watch region to be the intersection rect, rather than the full
// bounding rect. This way we can get to a point where we're still at a ratio of 1 while at rest.
//
// 3 happens if the element is completely outside the viewport. In this case we have no idea where
// to put the intersection region, and so should probably resort to: watching the full viewport,
// and listening for _any_ change to the threshold (threshold 0). This way we don't ever have to
// resort to polling or using a timeout, and can always be precise about when the element
// comes back into view.
//
// How do we handle transitions between these states? Is 2 distinguishable from 3 in most cases?
// Do we want to always update the watch region to be the intersection rect when the threshold is
// > 0?
//
// I don't think this approach will work actually... if we use a bounds equal to the current
// intersection, we'd miss any changes that "scroll through" that region. Is there any way to
// know whether we are clipped or not?
//
// I think maybe a better approach is to use an array of thresholds with fine grain
// (0, 0.1, 0.2, ... 1) and switch to that if we're not fully visible on the first observation.
// Then we just distinguish between that and not visible at all.
//
// Here's what we really want...
// If we're getting notifications that we are not 1 ratio, but the anchor position has not changed
// from the last time (we have to check this ourselves), then we can assume we're clipped and enter
// a "clipped" mode where we use fine grain thresholds to watch for changes.
//
// This new approach works better, and detects transitions well between the 3 states. However,
// we now have blind spots when the element is clipped. Since our clipping region is the size
// of the unclipped anchor, scrolling the page with the clipped element within it may not
// trigger any intersection changes since the visible part of the anchor can now slide around
// within the original bounding box without changing the intersection ratio (e.g. it can stay 1)
// anywhere the clipped region is fully visible in our box).
//
// I think this means we do need to resort to polling when we're in a clipped state. However, we
// can now handle the Zero state gracefully without any additional polling, since we can detect
// transitions from 0 -> non zero with our existing observer.
/**
 * Calls `onPositionChange` whenever the position of `anchor` changes. Uses `IntersectionObserver`s
 * to precisely watch for movement, rather than polling or listening to scroll/resize events.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 * @see https://samthor.au/2021/observing-dom/
 */
function observePosition({ anchor, onPositionChange, debug, }) {
    const ownerDocument = getOwnerDocument(anchor);
    const ownerWindow = getOwnerWindow(anchor);
    let unobserve;
    let rect;
    let visibility = 0 /* Visibility.FULL */;
    function setup() {
        unobserve?.();
        const lastRect = rect;
        rect = anchor.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) {
            // TODO: FIXME
            // I don't know why this happens sometimes, maybe because the ref changed? should we take
            // in the ref instead of the element directly so we can recover?
            // console.log('bailing due to zero size')
            return;
        }
        const root = ownerDocument;
        const rootEl = ownerDocument.documentElement;
        const rootMargin = computeMargin(rootEl, rect, visibility);
        const threshold = getThreshold(visibility);
        unobserve = observeIntersection({ element: anchor, root, rootMargin, threshold }, (entry, first) => {
            const ratio = entry.intersectionRatio;
            if (debug) {
                debugClear(ownerDocument);
                debugRect(ownerDocument, entry.boundingClientRect, 'blue');
                debugRect(ownerDocument, entry.rootBounds, 'green');
                debugRect(ownerDocument, entry.intersectionRect, 'yellow');
            }
            if (first) {
                if (ratio === 1) {
                    if (visibility !== 0 /* Visibility.FULL */) {
                        visibility = 0 /* Visibility.FULL */;
                        setup();
                    }
                    return;
                }
                // NOTE: all cases below are when ratio < 1, e.g. the anchor has
                // either already moved from the last `setup` call, or is clipped.
                const moved = !areRectsEqual(rect, lastRect);
                switch (visibility) {
                    case 0 /* Visibility.FULL */:
                        if (!moved) {
                            visibility = visibilityFromRatio(ratio);
                        }
                        break;
                    case 1 /* Visibility.CLIPPED */:
                    case 2 /* Visibility.NONE */:
                        if (!moved)
                            return;
                        visibility = visibilityFromRatio(ratio);
                        break;
                }
            }
            onPositionChange(entry);
            setup();
        }, ownerWindow);
    }
    setup();
    // The IntersectionObserver can't see an anchor that moves in lockstep with a viewport edge it's
    // pinned to (e.g. `position: fixed; bottom: 0`): the rootMargin is a fixed inset from that edge,
    // so the anchor and watch region shift together and the ratio never changes. On resize, check for
    // movement ourselves and report it; always rebuild the observer for the new viewport geometry.
    const removeResizeListener = addListener(ownerWindow, 'resize', () => {
        if (!areRectsEqual(anchor.getBoundingClientRect(), rect))
            onPositionChange();
        setup();
    });
    return () => {
        unobserve?.();
        removeResizeListener();
    };
}
function computeMargin(root, rect, visibility) {
    if (visibility === 2 /* Visibility.NONE */)
        return '0px';
    const { clientWidth, clientHeight } = root;
    const top = -Math.floor(rect.top);
    const right = -Math.floor(clientWidth - rect.right);
    const bottom = -Math.floor(clientHeight - rect.bottom);
    const left = -Math.floor(rect.left);
    return `${top}px ${right}px ${bottom}px ${left}px`;
}
function getThreshold(visibility) {
    switch (visibility) {
        case 0 /* Visibility.FULL */:
            return 1;
        case 1 /* Visibility.CLIPPED */:
            return getPartialThreshold();
        case 2 /* Visibility.NONE */:
            return 0;
    }
}
let _PARTIAL_THRESHOLD;
function getPartialThreshold() {
    return (_PARTIAL_THRESHOLD ??= range(101, (i) => i / 100));
}
const DEBUG_ID = 'debug-rect-root';
function debugClear(ownerDocument) {
    ownerDocument.getElementById(DEBUG_ID)?.remove();
}
function debugRect(ownerDocument, rect, color = 'red') {
    let root = ownerDocument.getElementById(DEBUG_ID);
    if (!root) {
        root = ownerDocument.createElement('div');
        root.id = DEBUG_ID;
        root.style.cssText = `
      pointer-events: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100dvw;
      height: 100dvh;
    `;
        ownerDocument.body.appendChild(root);
    }
    const div = ownerDocument.createElement('div');
    div.style.cssText = `
    position: fixed;
    top: ${rect.top}px;
    left: ${rect.left}px;
    width: ${rect.width}px;
    height: ${rect.height}px;
    border: 2px solid ${color};
    box-sizing: border-box;
  `;
    root.appendChild(div);
}
function areRectsEqual(a, b) {
    if (!b)
        return false;
    return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}
function visibilityFromRatio(ratio) {
    return ratio === 1 ? 0 /* Visibility.FULL */ : ratio === 0 ? 2 /* Visibility.NONE */ : 1 /* Visibility.CLIPPED */;
}
function observeIntersection({ element, ...init }, cb, ownerWindow) {
    let first = true;
    const IntersectionObserverConstructor = ownerWindow.IntersectionObserver ?? IntersectionObserver;
    const observer = new IntersectionObserverConstructor((entries) => {
        // In the case of multiple entries, we only care about the latest one, e.g. if the user
        // quickly scrolled back and forth and wound up going from a threshold < 1 back to 1 before
        // the observer had a chance to fire, we only care that we're now back to a threshold of 1
        // and don't need to do any repositioning.
        const entry = entries.at(-1);
        cb(entry, first);
        first = false;
    }, init);
    observer.observe(element);
    return () => observer.disconnect();
}

export { observePosition };
//# sourceMappingURL=observe-position.js.map
